import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import ImageUpload from '../components/ImageUpload';
import { useAuth } from '../context/AuthContext';
import { CheckCircle, Eye, EyeOff, User, Building2, Loader } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AU_CITIES_BY_STATE = {
  'NSW': ['Sydney','Newcastle','Wollongong','Central Coast','Maitland','Bathurst','Albury','Wagga Wagga','Port Macquarie','Dubbo','Orange','Tamworth','Armidale','Lismore','Coffs Harbour','Broken Hill'],
  'VIC': ['Melbourne','Geelong','Ballarat','Bendigo','Shepparton','Wodonga','Warrnambool','Mildura','Latrobe Valley','Horsham'],
  'QLD': ['Brisbane','Gold Coast','Sunshine Coast','Townsville','Cairns','Toowoomba','Mackay','Rockhampton','Ipswich','Bundaberg','Hervey Bay','Gladstone','Mount Isa'],
  'WA':  ['Perth','Mandurah','Bunbury','Geraldton','Kalgoorlie','Albany','Broome','Port Hedland','Karratha'],
  'SA':  ['Adelaide','Mount Gambier','Whyalla','Murray Bridge','Victor Harbor','Port Lincoln','Port Augusta'],
  'ACT': ['Canberra'],
  'TAS': ['Hobart','Launceston','Devonport','Burnie'],
  'NT':  ['Darwin','Palmerston','Alice Springs','Katherine'],
};
const AU_STATES = ['NSW','VIC','QLD','WA','SA','ACT','TAS','NT'];

const AMENITIES = [
  'WiFi','Internet','1 Bathroom','2 Bathrooms','1 Toilet','2 Toilets',
  'Common Room','AC','Heating','Washing Machine','Dryer',
  'Gym','Pool','Parking (Free)','Parking (Street)','Parking (Paid)',
  'Balcony','Courtyard','Garden','Security','Fireplace',
  'Study Room','Bike Storage','Couples OK','Pets Allowed',
];
const ROOM_FEATURES = ['1 Single Bed','2 Single Beds','1 Double Bed','1 Queen Bed','1 King Bed','Mattress Included','Wardrobe','Study Desk','Study Chair','Desk Lamp'];
const KITCHEN_FEATURES = ['Microwave','Toaster','Hot Water Jug / Kettle','Refrigerator','2 Refrigerators','Oven & Stovetop','Dishwasher','Coffee Machine','Shared Kitchen','Private Kitchen'];

const PillBtn = ({ label, active, onClick }) => (
  <button type="button" onClick={onClick}
    className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${active ? 'text-white border-transparent' : 'border-sky-200 text-gray-600 hover:border-[#094f6f]'}`}
    style={active ? { background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' } : {}}>
    {label}
  </button>
);

export default function SubmitListing() {
  const { user, token, login } = useAuth();
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Account fields (for new users)
  const [account, setAccount] = useState({ full_name: '', email: '', password: '', role: 'landlord' });

  // Property fields
  const [form, setForm] = useState({
    name: '', city: '', street_address: '', apt_no: '', suburb: '', postcode: '', state: '',
    price_per_week: '', room_type: 'Private Room', bedrooms: 1, bathrooms: 1,
    university_nearby: '', available_from: '', available_until: '',
    description: '', furnishing: 'Furnished', lease_term: 'Flexible',
    parking: 'Not Available', pets: 'Not Allowed', bills_included: 'Some Bills Included',
    amenities: [], room_features: [], kitchen_features: [], images: [],
    customAmenities: [], newAmenity: '',
    visibility: 'public',
  });

  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const pill = (key, val) => {
    const cur = form[key] || [];
    setF(key, cur.includes(val) ? cur.filter(x => x !== val) : [...cur, val]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      let authToken = token;
      let userId = user?.id;

      // Step 1: Create account if not logged in
      if (!user) {
        if (!account.full_name || !account.email || account.password.length < 8) {
          setError('Please fill in your name, email, and a password of at least 8 characters.');
          setSubmitting(false);
          return;
        }

        // Register
        const regRes = await fetch(`${API}/api/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: account.email,
            password: account.password,
            full_name: account.full_name,
            role: account.role,
          }),
        });
        const regData = await regRes.json();
        if (!regRes.ok) {
          const msg = typeof regData.detail === 'string' ? regData.detail
            : Array.isArray(regData.detail) ? regData.detail.map(e => e.msg).join(', ')
            : 'Registration failed.';
          if (msg.toLowerCase().includes('already registered')) {
            setError('This email is already registered. Please sign in first, then resubmit.');
          } else {
            setError(msg);
          }
          setSubmitting(false);
          return;
        }

        // Auto-login
        const loginForm = new URLSearchParams();
        loginForm.append('username', account.email);
        loginForm.append('password', account.password);
        const loginRes = await fetch(`${API}/api/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: loginForm.toString(),
        });
        const loginData = await loginRes.json();
        if (!loginRes.ok) { setError('Account created but login failed. Please sign in.'); setSubmitting(false); return; }

        login(loginData.access_token, {
          id: loginData.user_id, email: loginData.email,
          full_name: loginData.full_name, role: loginData.role,
        });
        authToken = loginData.access_token;
        userId = loginData.user_id;
      }

      // Step 2: Submit the property listing
      if (!form.city) { setError('Please select a city.'); setSubmitting(false); return; }

      const addressParts = [form.street_address, form.apt_no, form.suburb, form.state, form.postcode].filter(Boolean);
      const res = await fetch(`${API}/api/properties`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          address: addressParts.join(', '),
          price_per_week: parseFloat(form.price_per_week) || 0,
          bedrooms: parseInt(form.bedrooms),
          bathrooms: parseInt(form.bathrooms),
        }),
      });
      const propData = await res.json();
      if (!res.ok) {
        setError(typeof propData.detail === 'string' ? propData.detail : 'Submission failed. Please try again.');
        setSubmitting(false);
        return;
      }

      setSubmitted(true);
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Success screen ─────────────────────────────────────────────────────────
  if (submitted) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center bg-white rounded-2xl shadow-xl p-10 border border-sky-100">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-5" />
        <h2 className="text-2xl font-bold text-gray-900 mb-3">Listing Submitted!</h2>
        <p className="text-gray-500 mb-2 leading-relaxed">
          Your property listing has been received and is now <strong>pending admin review</strong>.
          We'll notify you by email once it's approved and live on RentalX.
        </p>
        <p className="text-sm text-gray-400 mb-6">Review typically takes 24 hours.</p>
        <div className="space-y-2">
          <Link to={user?.role === 'landlord' ? '/landlord-dashboard' : '/student-dashboard'}
            className="block w-full py-3 rounded-xl font-semibold text-white text-sm"
            style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}>
            Go to My Dashboard →
          </Link>
          <Link to="/" className="block w-full py-2.5 rounded-xl text-sm text-gray-500 border border-gray-200 hover:bg-gray-50">
            Back to Homepage
          </Link>
        </div>
      </div>
    </div>
  );

  // ── Main form ──────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="flex items-center justify-center gap-0 mb-4">
            <span className="text-3xl font-black tracking-tight" style={{ color: '#094f6f' }}>Rental</span>
            <img src="/rentalx-x-new.svg" alt="X"
              style={{ height: '2.2rem', width: 'auto', marginLeft: '0.06em', marginBottom: '0.03em', verticalAlign: 'middle' }} />
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Submit Your Property Listing</h1>
          <p className="text-gray-500 text-sm">
            Fill in the details below. Your listing will be reviewed by our team before going live.
          </p>
          {user && (
            <div className="mt-3 inline-flex items-center gap-2 bg-green-50 text-green-700 text-sm px-4 py-2 rounded-full border border-green-200">
              <CheckCircle className="w-4 h-4" />
              Signed in as <strong>{user.full_name || user.email}</strong>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">

          {/* Account section — only for guests */}
          {!user && (
            <Card className="border-sky-200 shadow-sm">
              <CardContent className="p-6">
                <h2 className="text-lg font-bold text-gray-900 mb-1 flex items-center gap-2">
                  <User className="w-5 h-5 text-[#094f6f]" />
                  Create Your Account
                </h2>
                <p className="text-sm text-gray-500 mb-4">
                  Already have an account?{' '}
                  <Link to="/login" className="text-[#094f6f] underline font-medium">Sign in first</Link>
                  {' '}then return to this page.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                    <Input value={account.full_name} onChange={e => setAccount(p => ({...p, full_name: e.target.value}))}
                      placeholder="e.g. John Smith" required className="border-sky-200" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                    <Input type="email" value={account.email} onChange={e => setAccount(p => ({...p, email: e.target.value}))}
                      placeholder="your@email.com" required className="border-sky-200" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Password * (min 8 characters)</label>
                    <div className="relative">
                      <Input type={showPassword ? 'text' : 'password'} value={account.password}
                        onChange={e => setAccount(p => ({...p, password: e.target.value}))}
                        placeholder="Create a password" required className="border-sky-200 pr-10" />
                      <button type="button" onClick={() => setShowPassword(s => !s)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Account Type</label>
                    <Select value={account.role} onValueChange={v => setAccount(p => ({...p, role: v}))}>
                      <SelectTrigger className="border-sky-200"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="landlord">Property Manager / Landlord</SelectItem>
                        <SelectItem value="working-professional">Working Professional</SelectItem>
                        <SelectItem value="student">Student</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Property details */}
          <Card className="border-sky-200 shadow-sm">
            <CardContent className="p-6 space-y-5">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#094f6f]" />
                Property Details
              </h2>

              {/* Name + City */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Property / Room Title *</label>
                  <Input value={form.name} onChange={e => setF('name', e.target.value)}
                    placeholder="e.g. Sunny Studio near UniMelb" required className="border-sky-200" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                  <Select value={form.city} onValueChange={v => setF('city', v)}>
                    <SelectTrigger className="border-sky-200"><SelectValue placeholder="Select city" /></SelectTrigger>
                    <SelectContent className="max-h-64">
                      {Object.entries(AU_CITIES_BY_STATE).map(([state, cities]) => (
                        <React.Fragment key={state}>
                          <div className="px-2 py-1 text-xs font-bold text-gray-400 uppercase bg-gray-50">{state}</div>
                          {cities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </React.Fragment>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Address */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                  <Input value={form.street_address} onChange={e => setF('street_address', e.target.value)}
                    placeholder="e.g. 123 Collins Street" className="border-sky-200" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Apt / Unit <span className="text-gray-400">(optional)</span></label>
                  <Input value={form.apt_no} onChange={e => setF('apt_no', e.target.value)}
                    placeholder="e.g. Unit 4" className="border-sky-200" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Suburb</label>
                  <Input value={form.suburb} onChange={e => setF('suburb', e.target.value)}
                    placeholder="e.g. Carlton" className="border-sky-200" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
                  <Input value={form.postcode} onChange={e => setF('postcode', e.target.value.replace(/\D/g,'').slice(0,4))}
                    placeholder="e.g. 3053" maxLength={4} className="border-sky-200" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                  <Select value={form.state} onValueChange={v => setF('state', v)}>
                    <SelectTrigger className="border-sky-200"><SelectValue placeholder="Select state" /></SelectTrigger>
                    <SelectContent>{AU_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              {/* Price + Type + Beds + Baths */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price/Week (AUD) *</label>
                  <Input type="number" value={form.price_per_week} onChange={e => setF('price_per_week', e.target.value)}
                    placeholder="350" required min="1" className="border-sky-200" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Room Type</label>
                  <Select value={form.room_type} onValueChange={v => setF('room_type', v)}>
                    <SelectTrigger className="border-sky-200"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['Private Room','Shared Room','Studio','1-Bedroom','2-Bedroom','Entire House','Entire Apartment'].map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bedrooms</label>
                  <Select value={String(form.bedrooms)} onValueChange={v => setF('bedrooms', parseInt(v))}>
                    <SelectTrigger className="border-sky-200"><SelectValue /></SelectTrigger>
                    <SelectContent>{[1,2,3,4,5].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bathrooms</label>
                  <Select value={String(form.bathrooms)} onValueChange={v => setF('bathrooms', parseInt(v))}>
                    <SelectTrigger className="border-sky-200"><SelectValue /></SelectTrigger>
                    <SelectContent>{[1,2,3,4].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              {/* Uni + Dates */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nearby University</label>
                  <Input value={form.university_nearby} onChange={e => setF('university_nearby', e.target.value)}
                    placeholder="e.g. University of Wollongong" className="border-sky-200" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Available From</label>
                  <Input type="date" value={form.available_from} onChange={e => setF('available_from', e.target.value)}
                    className="border-sky-200" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Available Until <span className="text-gray-400">(optional)</span></label>
                  <Input type="date" value={form.available_until} onChange={e => setF('available_until', e.target.value)}
                    min={form.available_from || undefined} className="border-sky-200" />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <textarea value={form.description} onChange={e => setF('description', e.target.value)}
                  placeholder="Describe your property — location highlights, house rules, what's included..." rows={4} required
                  className="w-full px-3 py-2.5 border border-sky-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/30 resize-y" />
              </div>

              {/* Amenities */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Amenities</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {[...AMENITIES, ...(form.customAmenities || [])].map(a => (
                    <PillBtn key={a} label={a} active={form.amenities.includes(a)}
                      onClick={() => setF('amenities', form.amenities.includes(a) ? form.amenities.filter(x => x !== a) : [...form.amenities, a])} />
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input value={form.newAmenity || ''} onChange={e => setF('newAmenity', e.target.value)}
                    placeholder="Add custom amenity..." className="border-sky-200 h-8 text-xs flex-1"
                    onKeyDown={e => { if (e.key === 'Enter' && form.newAmenity?.trim()) { e.preventDefault(); const a = form.newAmenity.trim(); setF('customAmenities', [...(form.customAmenities||[]),a]); setF('amenities',[...form.amenities,a]); setF('newAmenity',''); } }} />
                  <Button type="button" size="sm" className="h-8 text-xs text-white px-3" style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}
                    onClick={() => { const a=(form.newAmenity||'').trim(); if(!a)return; setF('customAmenities',[...(form.customAmenities||[]),a]); setF('amenities',[...form.amenities,a]); setF('newAmenity',''); }}>
                    + Add
                  </Button>
                </div>
              </div>

              {/* Room Features */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Room Features</label>
                <div className="flex flex-wrap gap-2">
                  {ROOM_FEATURES.map(f => <PillBtn key={f} label={f} active={(form.room_features||[]).includes(f)} onClick={() => pill('room_features', f)} />)}
                </div>
              </div>

              {/* Kitchen */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Kitchen Features</label>
                <div className="flex flex-wrap gap-2">
                  {KITCHEN_FEATURES.map(f => <PillBtn key={f} label={f} active={(form.kitchen_features||[]).includes(f)} onClick={() => pill('kitchen_features', f)} />)}
                </div>
              </div>

              {/* Photos */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Property Photos <span className="text-gray-400">(up to 5)</span></label>
                <ImageUpload images={form.images} onChange={urls => setF('images', urls)} folder="rentalx/listings" />
              </div>
            </CardContent>
          </Card>

          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">{error}</div>
          )}

          {/* Visibility toggle — positioned directly above the form action buttons */}
          <Card className="border-sky-200 shadow-sm">
            <CardContent className="p-6">
              <p className="text-sm font-bold text-gray-800 mb-3">Listing Visibility</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { val: 'public',  icon: '🌐', label: 'Public',  sub: 'Visible in all search results' },
                  { val: 'private', icon: '🔒', label: 'Private', sub: 'Shareable link only — not in search' },
                ].map(opt => (
                  <button key={opt.val} type="button" onClick={() => setF('visibility', opt.val)}
                    data-testid={`visibility-${opt.val}-btn`}
                    className={`flex items-start gap-2.5 p-3 rounded-xl border-2 text-left transition-all ${
                      form.visibility === opt.val ? 'border-[#094f6f] bg-white shadow-sm' : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}>
                    <span className="text-lg mt-0.5">{opt.icon}</span>
                    <div>
                      <p className={`text-sm font-bold ${form.visibility === opt.val ? 'text-[#094f6f]' : 'text-gray-700'}`}>{opt.label}</p>
                      <p className="text-xs text-gray-400">{opt.sub}</p>
                    </div>
                  </button>
                ))}
              </div>
              {form.visibility === 'private' && (
                <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 mt-3">
                  🔒 A unique private link will be generated after submission. Only people with the link can view this listing.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Notice */}
          <div className="text-center text-xs text-gray-400 flex items-center justify-center gap-1.5">
            <span>⏳</span>
            <span>Your listing will be reviewed by the RentalX team before going live (usually within 24 hours).</span>
          </div>

          <Button type="submit" disabled={submitting}
            className="w-full py-4 text-base font-bold text-white disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}>
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <Loader className="w-5 h-5 animate-spin" />
                {!user ? 'Creating account & submitting...' : 'Submitting listing...'}
              </span>
            ) : (
              !user ? 'Create Account & Submit Listing' : 'Submit Listing for Review'
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
